import { useId } from 'react'
import { Fieldset, Radio, ValidationMessage } from '@digdir/designsystemet-react'

type RadioOption = {
  value: string
  label: string
  hint?: string
  meta?: string
  disabled?: boolean
}

type RadioGroupProps = {
  /** Stable, programmatically focusable group target for error-summary links. */
  id?: string
  label: string
  hideLabel?: boolean
  name?: string
  value: string
  onChange: (value: string) => void
  options: RadioOption[]
  hint?: string
  error?: string
  disabled?: boolean
  required?: boolean
  variant?: 'plain' | 'cards'
}

export function RadioGroup({ id, label, hideLabel, name, value, onChange, options, hint, error, disabled, required, variant = 'plain' }: RadioGroupProps) {
  const baseId = useId()
  const groupId = id ?? `${baseId}group`
  const legendId = `${groupId}-legend`
  const hintId = hint ? `${groupId}-hint` : undefined
  const errorId = error ? `${groupId}-err` : undefined

  return (
    <Fieldset id={groupId} tabIndex={-1} className="choice-group" disabled={disabled}
      aria-labelledby={legendId} aria-describedby={[hintId, errorId].filter(Boolean).join(' ') || undefined}>
      <Fieldset.Legend id={legendId} className={hideLabel ? 'sr-only' : 'field__lbl'}>{label}</Fieldset.Legend>
      {hint && <Fieldset.Description className="field__hint" id={hintId}>{hint}</Fieldset.Description>}
      <div className="choice-group__options">
        {options.map(option => (
          <Radio key={option.value} className={`radio-field${variant === 'cards' ? ' radio-field--card' : ''}`}
            variant={variant === 'cards' ? 'outline' : undefined}
            // DS Field observes its own subtree, so refresh it when the shared error appears/disappears.
            label={<span key={error ? 'invalid' : 'valid'}>{option.label}</span>}
            name={name ?? baseId} value={option.value} checked={value === option.value}
            disabled={disabled || option.disabled} required={required}
            onChange={disabled || option.disabled ? undefined : e => onChange(e.target.value)}
            description={option.hint || option.meta ? <span className="choice-copy">
              {option.hint && <span>{option.hint}</span>}
              {option.hint && option.meta && ' '}
              {option.meta && <span className="radio-field__meta">{option.meta}</span>}
            </span> : undefined} />
        ))}
      </div>
      {error && <ValidationMessage className="field__err" id={errorId} role="alert">{error}</ValidationMessage>}
    </Fieldset>
  )
}
